/**
 * useNewAppointment — single-call mutation for the unified create modal.
 *
 * Resolves the right Graph endpoint(s) per channel:
 *   - inBranch / phone : POST /me/events
 *   - teams            : POST /me/onlineMeetings + POST /me/events
 *   - bookingsLink     : returns the public Bookings URL (no Graph write)
 *
 * In zero-flag mode (most environments today) the mutation returns a
 * synthesized success so the UX flows end-to-end without server traffic.
 */

import { useCallback, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import {
  createCalendarEvent,
} from '../services/graphCalendarClient';
import {
  createOnlineMeeting,
} from '../services/graphMeetingsClient';
import { readLocalBookingsConfig } from '../services/graphBookingsClient';
import type { NewAppointmentInput, NewAppointmentResult } from '../types';

function liveEnabled(): boolean {
  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  return env['VITE_FEATURE_APPOINTMENTS_LIVE'] === 'true';
}

function localToUtcIso(dateIso: string, timeHHmm: string, durationMin: number): { startIsoLocal: string; endIsoLocal: string; startUtc: string; endUtc: string } {
  // Build a local Date at the chosen wall-clock time, then derive UTC ISOs
  // for endpoints that need them (online meetings).
  const [hh, mm] = timeHHmm.split(':');
  const [y, m, d] = dateIso.split('-');
  const local = new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(hh ?? '9'),
    Number(mm ?? '0'),
    0,
    0,
  );
  const endLocal = new Date(local.getTime() + durationMin * 60_000);
  const fmt = (d: Date): string =>
    `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}` +
    `T${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}:00`;
  return {
    startIsoLocal: fmt(local),
    endIsoLocal: fmt(endLocal),
    startUtc: local.toISOString(),
    endUtc: endLocal.toISOString(),
  };
}

export interface UseNewAppointmentResult {
  busy: boolean;
  error: string | null;
  create: (input: NewAppointmentInput) => Promise<NewAppointmentResult>;
}

function nextConfirmationCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 5; i += 1) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `PCB-${s}`;
}

export function useNewAppointment(): UseNewAppointmentResult {
  const { token } = useAuth();
  const { config } = useConfig();
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(async (input: NewAppointmentInput): Promise<NewAppointmentResult> => {
    setBusy(true);
    setError(null);
    try {
      // ---- Bookings link channel ---------------------------------------
      if (input.channel === 'bookingsLink') {
        const rows = readLocalBookingsConfig();
        const cfg = rows.find((r) => r.branchCode === input.branchCode);
        const link = cfg?.publicBookingUrl;
        return {
          ok: true,
          message: link ? `Bookings link ready to share: ${link}` : 'Bookings business not yet configured for this branch.',
          ...(link ? { bookingsLink: link } : {}),
        };
      }

      // ---- Without LIVE flag — simulate success ------------------------
      if (!liveEnabled() || !token) {
        return {
          ok: true,
          message: `Appointment created (simulated, demo mode). Confirmation ${nextConfirmationCode()}.`,
        };
      }

      const branch = config.branches.find((b) => b.branchCode === input.branchCode);
      const locationDisplay = input.channel === 'teams'
        ? 'Microsoft Teams meeting'
        : input.channel === 'phone'
          ? 'Phone call'
          : (branch?.branchName ?? input.branchCode);

      const slot = localToUtcIso(input.date, input.time, input.durationMin);

      // ---- Teams channel: meeting first, then calendar event -----------
      let bodyHtml = `<p>${input.subject}</p>`;
      if (input.notes) bodyHtml += `<p><strong>Notes:</strong> ${input.notes}</p>`;

      if (input.channel === 'teams') {
        try {
          const meet = await createOnlineMeeting(token, {
            subject: `${input.subject} · ${input.customerName}`,
            startUtc: slot.startUtc,
            endUtc: slot.endUtc,
          });
          bodyHtml += `<p><a href="${meet.joinWebUrl}">Join Teams meeting</a></p>`;
        } catch (e) {
          // Downgrade: still create the calendar event (without join link)
          // but flag the message so the FSR knows.
          const msg = e instanceof Error ? e.message : String(e);
          await createCalendarEvent(token, {
            subject: `${input.subject} · ${input.customerName}`,
            bodyHtml,
            startIsoLocal: slot.startIsoLocal,
            endIsoLocal: slot.endIsoLocal,
            timeZone: 'America/Chicago',
            locationDisplay,
            attendees: input.customerEmail ? [{ name: input.customerName, email: input.customerEmail }] : [],
          });
          return {
            ok: true,
            message: `Calendar event created, but Teams meeting could not be created (${msg}). Add a join link manually if needed.`,
          };
        }
      }

      // ---- Calendar event ----------------------------------------------
      await createCalendarEvent(token, {
        subject: `${input.subject} · ${input.customerName}`,
        bodyHtml,
        startIsoLocal: slot.startIsoLocal,
        endIsoLocal: slot.endIsoLocal,
        timeZone: 'America/Chicago',
        locationDisplay,
        attendees: input.customerEmail ? [{ name: input.customerName, email: input.customerEmail }] : [],
      });
      return { ok: true, message: `Appointment created. Confirmation ${nextConfirmationCode()}.` };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      return { ok: false, message: msg };
    } finally {
      setBusy(false);
    }
  }, [token, config.branches]);

  return { busy, error, create };
}
